<script lang="ts">
	import {
		filterScenesInitial,
		generateThermostatIcon,
		sortBy,
		sortListByPossition,
	} from 'src/components/Lists/list-helpers';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { format } from 'svelte-i18n';
	import { scenes } from 'src/stores/lists/scenes';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import type { DashWidgetEditType, ListRefType } from '../../dash-types';
	import { checkLightGroupHasSingleSlider } from '../../dash-lights-helpers';
	import {
		filterDevicesWithControl,
		filterDevicesWithNoMediaPlayer,
		MAX_CARDS_IN_WIDGET,
	} from '../../dash-helpers';
	import { getRoomsWithPmDevices } from '../../dash-energy-helpers';
	import { getDevicesWithEnvironmentReadings } from '../../dash-environment-helpers';
	import { isCameraDevice } from 'src/helpers/interface_helpers';
	import { getCameraSelectionLimit } from '../../dash-camera-helpers';
	import DashWidgetLimitAlert from '../Components/DashWidgetLimitAlert.svelte';
	import { capFirstChar } from 'src/helpers/utilities';

	export let editItem: DashWidgetEditType;
	export let allItemsRefs: ListRefType;

	const sortScenesListByPossition = (list: any) => {
		return list.sort((x: any, y: any) => {
			return sortBy(x._meta, y._meta, 'position');
		});
	};

	const handleSelectItem = (
		item: any,
		id: number | string,
		type: DashWidgetEditType['widgetType'],
	) => {
		const selectedCount = editItem.selectItems.length;
		const maxItems =
			type === 'dashCamera'
				? getCameraSelectionLimit(editItem.cameraConfig?.variant)
				: MAX_CARDS_IN_WIDGET;

		//if the item is already checked and it is in selectItems than remove it else add it
		if (editItem.selectItems.find((item) => item.id === id) !== undefined) {
			editItem.selectItems = editItem.selectItems.filter(
				(item) => item.id.toString() !== id.toString(),
			);
		} else {
			if (selectedCount >= maxItems) return;

			const maxPosition = editItem.selectItems.length
				? Math.max(...editItem.selectItems.map((item) => item.position)) + 1
				: 0;
			editItem.selectItems = [
				...editItem.selectItems,
				{
					type: type,
					itemType: item.type,
					id: id.toString(),
					position: maxPosition,
				},
			];
		}
	};

	const filterDevicesByRoomID = (devices: any) => {
		let filteredDevicesByRoom = {};

		devices.forEach((device: any, i) => {
			const roomID = device.room_id;

			const deviceData = {
				name: `${device.name}${
					!devices_status.getByRealId(device.id)?.online ? ' - Offline' : ''
				}`,
				id: device.id,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image,
				icon: device.icon,
				backgroundColor: device.backgroundColor,
				category: device?.category,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID] = {
					cards: [...filteredDevicesByRoom[roomID].cards, deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					position: $rooms[roomID]?.position,
				};
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					position: $rooms[roomID]?.position,
				};
			}
		});

		// Remove devices that are not assigned to any room yet.
		// delete filteredDevicesByRoom['-20'];

		return Object.values(filteredDevicesByRoom);
	};

	$: list =
		getListByType(editItem.widgetType, editItem.selectItems, editItem.selectItems.length) || [];

	const getListByType = (type: DashWidgetEditType['widgetType'], items, counter: number) => {
		const maxCards =
			type === 'dashCamera'
				? getCameraSelectionLimit(editItem.cameraConfig?.variant)
				: MAX_CARDS_IN_WIDGET;
		const disableCard = counter >= maxCards;
		const isCardChecked = (id) => items.some((obj) => obj.id === id);

		if (type === 'dashRoom' || type === 'dashEnergyRoomChart') {
			const roomList =
				type === 'dashEnergyRoomChart'
					? getRoomsWithPmDevices()
					: sortListByPossition(Object.values($rooms));
			return roomList.map((room) => {
				return {
					...room,
					id: room?.id?.toString(),
					disabled: !isCardChecked(room.id) && disableCard,
					type: 'rooms',
				};
			});
		} else if (type === 'dashGroup') {
			return sortListByPossition(Object.values($groups)).map((group) => {
				return {
					...group,
					id: group?.id?.toString(),
					disabled: !isCardChecked(group.id) && disableCard,
					type: 'groups',
				};
			});
		} else if (type === 'dashLight') {
			return sortListByPossition(Object.values($groups))
				.filter((group) => group.type === 'light')
				.filter((lightGroup) => checkLightGroupHasSingleSlider(lightGroup))
				.map((group) => {
					return {
						...group,
						id: group?.id?.toString(),
						disabled: !isCardChecked(group.id) && disableCard,
						type: 'groups',
					};
				});
		} else if (type === 'dashScene') {
			return sortScenesListByPossition(
				Object.values($scenes)
					.filter(filterScenesInitial)
					.map((scene: any) => {
						return {
							...scene,
							name: scene._meta.name,
							image: scene._meta.image,
							backgroundColor: scene._meta.backgroundColor,
							id: scene?._id?.toString(),
							disabled: !isCardChecked(scene.id) && disableCard,
							type: 'scenes',
						};
					}),
			);
		} else if (type === 'dashParameterDev' || type === 'dashQuickDev' || type === 'dashCardDev') {
			return sortListByPossition(
				filterDevicesByRoomID(filterDevicesWithNoMediaPlayer(Object.values($devices))),
			)?.map((room) => {
				return {
					...room,
					cards: room.cards.map((card) => {
						return {
							...card,
							id: card?.id?.toString(),
							disabled: !isCardChecked(card.id) && disableCard,
							type: 'devices',
						};
					}),
				};
			});
		} else if (type === 'dashControlDev') {
			return sortListByPossition(
				filterDevicesByRoomID(filterDevicesWithControl(Object.values($devices))),
			)?.map((room) => {
				return {
					...room,
					cards: room.cards.map((card) => {
						return {
							...card,
							id: card?.id?.toString(),
							disabled: !isCardChecked(card.id) && disableCard,
							type: 'devices',
						};
					}),
				};
			});
		} else if (type === 'dashCamera') {
			return sortListByPossition(
				filterDevicesByRoomID(Object.values($devices).filter(isCameraDevice)),
			)?.map((room) => {
				return {
					...room,
					cards: room.cards.map((card) => {
						return {
							...card,
							id: card?.id?.toString(),
							disabled: !isCardChecked(card.id) && disableCard,
							type: 'devices',
						};
					}),
				};
			});
		} else if (type === 'dashAlarm') {
			return sortScenesListByPossition(
				Object.values($alarms).map((alarm: any) => {
					return {
						...alarm,
						name: alarm._meta.name,
						image: alarm._meta.image,
						backgroundColor: alarm._meta.backgroundColor,
						id: alarm?._id?.toString(),
						disabled: !isCardChecked(alarm.id) && disableCard,
						type: 'alarms',
					};
				}),
			);
		} else if (type === 'dashMedia') {
			return sortListByPossition(filterDevicesByRoomID(Object.values($devices)))
				?.map((room) => {
					return {
						...room,
						cards: room.cards
							.filter((device) => device?.category === 'media_player')
							.map((card) => {
								return {
									...card,
									id: card?.id?.toString(),
									disabled: !isCardChecked(card.id) && disableCard,
									type: 'devices',
								};
							}),
					};
				})
				?.filter((r) => r.cards.length);
		} else if (type === 'dashThermostat') {
			return sortScenesListByPossition(
				Object.values($temp_schemes).map((thermostat: any) => {
					return {
						...thermostat,
						name: thermostat._meta.name,
						icon: generateThermostatIcon(thermostat, 'fat'),
						id: thermostat?._id?.toString(),
						disabled: !isCardChecked(thermostat.id) && disableCard,
						type: 'temp_schemes',
					};
				}),
			);
		} else if (type === 'dashEnvironment') {
			return sortListByPossition(
				filterDevicesByRoomID(getDevicesWithEnvironmentReadings(Object.values($devices))),
			)
				?.map((room) => {
					return {
						...room,
						cards: room.cards.map((card) => {
							return {
								...card,
								id: card?.id?.toString(),
								disabled: !isCardChecked(card.id) && disableCard,
								type: 'devices',
							};
						}),
					};
				})
				?.filter((room) => room.cards.length);
		}
	};
</script>

<DashWidgetLimitAlert
	counter={editItem.selectItems.length}
	limit={editItem.widgetType === 'dashCamera'
		? getCameraSelectionLimit(editItem.cameraConfig?.variant)
		: MAX_CARDS_IN_WIDGET}
/>

<SelectList
	title={capFirstChar(
		$format('label_selected', {
			default: 'Selected:',
		}),
	)}
	titleHighlight={editItem.selectItems.length}
	{list}
	variant={'checkbox'}
	selectedCardsId={editItem.selectItems}
	bind:allCardsRefs={allItemsRefs}
	on:click={(e) => handleSelectItem(e.detail, e.detail.id, editItem.widgetType)}
/>
